import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import type { RegisterOptions } from "react-hook-form";
import { mergeRefs } from "@circle-react/helpers/refHelpers";
import type { SupportedSection } from "./Editor";
import { isSupportedSection } from "./Editor";
import { CustomHtmlEditor } from "./index";

interface CodeValues {
  body: string;
  head: string;
}

export interface FormCustomHtmlEditorProps {
  className?: string;
  name: string;
  rules?: RegisterOptions;
  sectionFieldName?: string;
}

export const FormCustomHtmlEditor = forwardRef<
  HTMLTextAreaElement,
  FormCustomHtmlEditorProps
>(({ name, rules = {}, className, sectionFieldName }, ref) => {
  const { register, setValue, control } = useFormContext();
  const [activeSection, setActiveSection] = useState<SupportedSection>("body");

  const { ref: formRef } = register(name, rules);

  useEffect(() => {
    if (sectionFieldName) {
      register(sectionFieldName);
      setValue(sectionFieldName, activeSection, { shouldDirty: false });
    }
  }, [sectionFieldName, activeSection, register, setValue]);

  const formValue = useWatch({
    control,
    name,
    defaultValue: {
      head: `<!-- Add <head> content here like:
  - <title> for page title
  - <meta> tags for SEO
  - <link> tags for external CSS
  - <style> tags for custom CSS
  - <script> tags for JavaScript -->`,
      body: `<!-- Add <body> content here like:
  - HTML elements (<div>, <p>, etc.)
  - Custom layout and structure
  - Text content and images
  - Embedded content and widgets -->`,
    },
  });

  const { head = "", body = "" } = formValue || {};

  const currentContent = useMemo(() => {
    switch (activeSection) {
      case "head":
        return head;
      case "body":
        return body;
      default:
        return "";
    }
  }, [activeSection, head, body]);

  const handleChange = useCallback(
    (newContent: string) => {
      const updatedValue: Partial<CodeValues> = {
        head: activeSection === "head" ? newContent : head,
        body: activeSection === "body" ? newContent : body,
      };

      setValue(name, updatedValue, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [activeSection, head, body, name, setValue],
  );

  const handleSectionChange = useCallback(
    (newSection: string) => {
      if (!isSupportedSection(newSection)) return;
      setActiveSection(newSection);
      if (sectionFieldName) {
        setValue(sectionFieldName, newSection, { shouldDirty: true });
      }
    },
    [sectionFieldName, setValue],
  );

  const stringifiedValue = useMemo(
    () => JSON.stringify({ head, body }),
    [head, body],
  );

  return (
    <div className={className}>
      <CustomHtmlEditor
        value={currentContent}
        onChange={handleChange}
        onSectionChange={handleSectionChange}
        initialSection={activeSection}
        className="mb-1"
      />
      <textarea
        ref={mergeRefs([ref, formRef])}
        name={name}
        value={stringifiedValue}
        readOnly
        hidden
      />
    </div>
  );
});

FormCustomHtmlEditor.displayName = "Form.CustomHtmlEditor";
